/**
 * EPJ Edit Service - Frontend client for structured EPJ/EPLAN file editing
 * 
 * Workflow:
 * 1. Upload raw EPJ file to backend for storage
 * 2. AI generates structured edit commands (JSON operations)
 * 3. Preview operations (dry run)
 * 4. Apply operations to stored EPJ
 * 5. Download modified EPJ file
 */

import { apiConfig } from "../config/apiConfig.js";
import { getCurrentUserEmail, getApiToken } from "./authService.js";
import { EPLAN_ATTRIBUTE_LABELS } from "../constants/eplanAttributes.js";

const BASE_URL = apiConfig.baseUrl;

/**
 * Upload a raw EPJ file to the backend for editing.
 * The file is stored server-side so the DOM can be manipulated for large files.
 * @param {File} file - The EPJ file to upload
 * @param {Function} progressCallback - Optional progress callback(percent, message)
 * @returns {Object} Upload result with file summary
 */
export async function uploadEpjForEditing(file, progressCallback = null) {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) throw new Error("Nicht angemeldet");

    const allowedExtensions = ['.epj', '.xml'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(ext)) {
        throw new Error(`Nur EPJ/XML-Dateien erlaubt. Erkannt: ${ext}`);
    }

    if (progressCallback) progressCallback(5, "Datei wird gelesen...");

    // Read as text
    const content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden"));
        reader.readAsText(file);
    });

    if (progressCallback) progressCallback(40, "EPJ wird zum Server gesendet...");

    const response = await fetch(`${BASE_URL}/api/epj/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getApiToken()}` },
        body: JSON.stringify({
            fileName: file.name,
            content,
            userId: userEmail
        })
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error || `Upload fehlgeschlagen (${response.status})`);
    }

    if (progressCallback) progressCallback(100, "EPJ gespeichert!");

    return await response.json();
}

/**
 * List stored EPJ files available for editing
 * @returns {Array} List of stored files
 */
export async function listEpjFiles() {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) throw new Error("Nicht angemeldet");

    const response = await fetch(`${BASE_URL}/api/epj/files/${encodeURIComponent(userEmail)}`, {
        headers: { Authorization: `Bearer ${await getApiToken()}` }
    });

    if (!response.ok) {
        if (response.status === 404) return [];
        throw new Error("Fehler beim Laden der EPJ-Dateien");
    }

    const result = await response.json();
    return result.files || [];
}

/**
 * Get a structured overview of an EPJ file (for AI context)
 * @param {string} fileName
 * @returns {Object} File overview with structure summary
 */
export async function getEpjOverview(fileName) {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) throw new Error("Nicht angemeldet");

    const response = await fetch(
        `${BASE_URL}/api/epj/overview/${encodeURIComponent(userEmail)}/${encodeURIComponent(fileName)}`,
        { headers: { Authorization: `Bearer ${await getApiToken()}` } }
    );

    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error || "Übersicht konnte nicht geladen werden");
    }

    return await response.json();
}

/**
 * Search for specific nodes in the EPJ file
 * @param {string} fileName
 * @param {Object} criteria - { tag?: string, attributes?: { key: value } }
 * @returns {Array} Matching nodes with paths
 */
export async function searchEpjNodes(fileName, criteria) {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) throw new Error("Nicht angemeldet");

    const response = await fetch(`${BASE_URL}/api/epj/search-nodes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getApiToken()}` },
        body: JSON.stringify({
            userId: userEmail,
            fileName,
            criteria
        })
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error || "Suche fehlgeschlagen");
    }

    const result = await response.json();
    return result.matches || [];
}

/**
 * Preview edit operations without applying them (dry run)
 * @param {string} fileName
 * @param {Array} operations - Array of edit operations
 * @returns {Object} Preview result with per-operation status
 */
export async function previewEpjEdits(fileName, operations) {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) throw new Error("Nicht angemeldet");

    const response = await fetch(`${BASE_URL}/api/epj/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getApiToken()}` },
        body: JSON.stringify({
            userId: userEmail,
            fileName,
            operations
        })
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error || "Vorschau fehlgeschlagen");
    }

    return await response.json();
}

/**
 * Apply edit operations to a stored EPJ file
 * @param {string} fileName
 * @param {Array} operations - Array of { op, path, attribute?, value?, element?, attributes? }
 * @returns {Object} Result with per-operation status
 */
export async function applyEpjEdits(fileName, operations) {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) throw new Error("Nicht angemeldet");

    const response = await fetch(`${BASE_URL}/api/epj/edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getApiToken()}` },
        body: JSON.stringify({
            userId: userEmail,
            fileName,
            operations
        })
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error || "Bearbeitung fehlgeschlagen");
    }

    return await response.json();
}

/**
 * Download the modified EPJ file
 * Triggers a browser download of the file.
 * @param {string} fileName
 */
export async function downloadEpjFile(fileName) {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) throw new Error("Nicht angemeldet");

    const url = `${BASE_URL}/api/epj/download/${encodeURIComponent(userEmail)}/${encodeURIComponent(fileName)}`;
    const response = await fetch(url, { headers: { Authorization: `Bearer ${await getApiToken()}` } });

    if (!response.ok) {
        throw new Error("Download fehlgeschlagen");
    }

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
}

/**
 * Restore an EPJ file from its backup (undo all edits)
 * @param {string} fileName
 * @returns {Object} Restore result
 */
export async function restoreEpjFromBackup(fileName) {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) throw new Error("Nicht angemeldet");

    const response = await fetch(`${BASE_URL}/api/epj/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getApiToken()}` },
        body: JSON.stringify({
            userId: userEmail,
            fileName
        })
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error || "Wiederherstellung fehlgeschlagen");
    }

    return await response.json();
}

/**
 * Delete a stored EPJ file and its backup
 * @param {string} fileName
 * @returns {Object} Delete result
 */
export async function deleteEpjFile(fileName) {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) throw new Error("Nicht angemeldet");

    const response = await fetch(
        `${BASE_URL}/api/epj/files/${encodeURIComponent(userEmail)}/${encodeURIComponent(fileName)}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${await getApiToken()}` } }
    );

    if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error || "Löschen fehlgeschlagen");
    }

    return await response.json();
}

/**
 * Get the edit operations schema (for constructing AI prompts)
 * @returns {Object} JSON schema describing allowed operations
 */
export async function getEpjEditSchema() {
    const response = await fetch(`${BASE_URL}/api/epj/schema`);

    if (!response.ok) {
        throw new Error("Schema konnte nicht geladen werden");
    }

    return await response.json();
}

/**
 * Build a system prompt for the AI to generate EPJ edit operations.
 * Combines the edit schema with file context from RAG search results.
 * 
 * @param {string} fileName - Name of the EPJ file being edited
 * @param {Object} fileOverview - Structure overview from getEpjOverview()
 * @param {string|null} ragContext - Optional RAG context from document search
 * @returns {string} System prompt for the AI
 */
export function buildEpjEditPrompt(fileName, fileOverview, ragContext = null) {
    // Extract first real A1 values from overview for realistic examples
    let examplePageA1 = '1';
    let exampleDeviceA1 = '17/7';
    let examplePathFromPage = '';
    try {
        const overview = fileOverview?.overview || [];
        for (const item of overview) {
            if (item.tag === 'EplanPxfRoot' && item.children) {
                for (const child of item.children) {
                    if (child.tag === 'O14' && child.children) {
                        for (const page of child.children) {
                            if (page.tag === 'O4' && page.attributes?.A1 && page.devices?.length > 0) {
                                examplePageA1 = page.attributes.A1;
                                for (const dev of page.devices) {
                                    if (dev.tag === 'O17' && dev.attributes?.A1) {
                                        exampleDeviceA1 = dev.attributes.A1;
                                        examplePathFromPage = dev.pathFromPage || '';
                                        break;
                                    }
                                }
                                break;
                            }
                        }
                        break;
                    }
                }
                break;
            }
        }
    } catch (e) { /* use defaults */ }

    // Build a flat device index: one row per device that has a BMK
    // This lets the AI instantly look up any BMK without scanning the nested JSON
    const deviceIndex = [];
    try {
        const overview = fileOverview?.overview || [];
        for (const item of overview) {
            if (item.tag !== 'EplanPxfRoot' || !item.children) continue;
            for (const child of item.children) {
                if (child.tag !== 'O14' || !child.children) continue;
                for (const page of child.children) {
                    if (page.tag !== 'O4' || !page.attributes?.A1 || !page.devices) continue;
                    const pageA1 = page.attributes.A1;
                    for (const dev of page.devices) {
                        if (!dev.bmk) continue;
                        const deviceA1 = dev.attributes?.A1 || '';
                        const path = dev.pathFromPage
                            ? `/EplanPxfRoot[0]/O14[0]/O4{A1=${pageA1}}/${dev.pathFromPage}/O17{A1=${deviceA1}}`
                            : `/EplanPxfRoot[0]/O14[0]/O4{A1=${pageA1}}/O17{A1=${deviceA1}}`;
                        deviceIndex.push({
                            bmk: dev.bmk,
                            prefix: dev.bmkPrefix || '',
                            number: dev.bmkNumber || '',
                            path,
                            article: dev.article || ''
                        });
                    }
                }
                break;
            }
            break;
        }
    } catch (e) { /* ignore */ }

    const deviceIndexText = deviceIndex.length > 0
        ? deviceIndex.map(d =>
            `  ${d.bmk.padEnd(12)} Pfad: ${d.path}  | P20013="${d.prefix}" P20014="${d.number}"${d.article ? `  Artikel: ${d.article}` : ''}`
          ).join('\n')
        : '  (keine Geräte mit BMK gefunden)';

    // Build the example path with or without O26 nesting
    const exampleDevicePath = examplePathFromPage
        ? `/EplanPxfRoot[0]/O14[0]/O4{A1=${examplePageA1}}/${examplePathFromPage}/O17{A1=${exampleDeviceA1}}`
        : `/EplanPxfRoot[0]/O14[0]/O4{A1=${examplePageA1}}/O17{A1=${exampleDeviceA1}}`;

    const schema = {
        operations: [
            {
                op: "set-attribute",
                path: `${exampleDevicePath}/P11[0]`,
                attribute: "P20100_1",
                value: "NEW.ARTICLE.123"
            },
            {
                op: "set-attribute",
                path: `${exampleDevicePath}/P144[0]`,
                attribute: "P20013",
                value: "K"
            },
            {
                op: "copy-node",
                sourcePath: exampleDevicePath,
                targetPath: `/EplanPxfRoot[0]/O14[0]/O4{A1=${examplePageA1}}`,
                attributeOverrides: { "A1": "17/99001" },
                childAttributeOverrides: [
                    { childTag: "P144", attributes: { "P20013": "QA", "P20014": "11" } },
                    { childTag: "P150", attributes: { "P20013": "QA", "P20014": "11" } }
                ]
            },
            {
                op: "remove-node",
                path: exampleDevicePath
            }
        ]
    };

    let prompt = `Du bist ein EPLAN-Experte. Du bearbeitest die EPJ-Datei "${fileName}".

WICHTIG: Du darfst KEIN rohes XML erzeugen. Stattdessen gibst du strukturierte Bearbeitungsbefehle als JSON zurück.

## Geräte-Index (VERWENDE DIESE TABELLE für BMK-Suche)
Jede Zeile = ein Gerät mit vollständigem Pfad und exakten P20013/P20014-Werten:
${deviceIndexText}

## Erlaubte Operationen

Deine Antwort MUSS ein JSON-Objekt mit diesem Format sein:
\`\`\`json
{
  "text": "Beschreibung der Änderungen auf Deutsch",
  "operations": ${JSON.stringify(schema.operations, null, 2)}
}
\`\`\`

## Pfad-Syntax
- Index-basiert: \`/TagName[index]\` - Auswahl nach Position unter gleichnamigen Geschwistern
- Attribut-Filter: \`/TagName{AttrCode=Wert}\` - Auswahl nach Attributwert
- WICHTIG: O17-Geräte können in O26-Containern verschachtelt sein! Verwende das "pathFromPage"-Feld des Geräts im Pfad.
- Pfade gehen von EplanPxfRoot bis zum Zielknoten, z.B.: \`${exampleDevicePath}/P11[0]\`
- WICHTIG: Verwende IMMER echte A1-Werte aus der Dateistruktur unten, NIEMALS Platzhalter!

## EPLAN EPJ-Struktur

### Knoten-Hierarchie (typisch)
\`\`\`
EplanPxfRoot
  └─ O14  (Projektstruktur)
       └─ O4  (Seite / Page)
            └─ [optional: O26/O26 Container]
                 └─ O17  (Gerät / Symbol auf der Seite)
                      ├─ P11   (Eigenschaftsblock: Artikel, Funktionstext)
                      ├─ P144  (BMK Hauptfunktion: Prefix + Nummer)
                      ├─ P150  (BMK Einbauort: Prefix + Nummer)
                      ├─ S89x5 (Symboldarstellung)
                      ├─ S61x183 (Verbindungsdaten)
                      └─ S40x1201 / S39x761 (weitere Symboldaten)
\`\`\`

### Gerät kopieren (copy-node) — PFLICHT für neue Geräte
Ein neues Gerät kann NUR durch Kopieren eines vorhandenen erstellt werden.
NIEMALS add-child für O17 verwenden — das erzeugt eine leere Hülle ohne Symboldaten.
Stattdessen copy-node verwenden:
- sourcePath: Pfad zum Quellgerät (aus dem Geräte-Index)
- targetPath: Seite, auf die das Gerät eingefügt wird
- attributeOverrides: Neuen A1-Wert setzen (Format "17/NNNNN", z.B. die Quell-ID + 1)
- childAttributeOverrides: P144 und P150 mit neuem BMK überschreiben

Beispiel: QA11 als Kopie von QA10 einfügen:
\`\`\`json
{
  "op": "copy-node",
  "sourcePath": "/EplanPxfRoot[0]/O14[0]/O4{A1=4/102}/O17{A1=17/4524}",
  "targetPath": "/EplanPxfRoot[0]/O14[0]/O4{A1=4/102}",
  "attributeOverrides": { "A1": "17/4525" },
  "childAttributeOverrides": [
    { "childTag": "P144", "attributes": { "P20013": "QA", "P20014": "11" } },
    { "childTag": "P150", "attributes": { "P20013": "QA", "P20014": "11" } }
  ]
}
\`\`\`
Für den neuen A1: nimm den Quell-A1 und addiere 1 (z.B. 17/4524 → 17/4525).

### WICHTIG: Verschachtelte Geräte und pathFromPage
Geräte (O17) können DIREKT unter O4 liegen oder in O26-Containern verschachtelt sein.
In der Dateistruktur unten hat jedes Gerät optional ein "pathFromPage"-Feld.
Dieses Feld enthält den Pfad von O4 zum O17, z.B.: "O26{A1=26/6961}/O26{A1=26/6962}"

Pfad-Aufbau:
- OHNE pathFromPage: /EplanPxfRoot[0]/O14[0]/O4{A1=<seitenA1>}/O17{A1=<geräteA1>}/...
- MIT pathFromPage: /EplanPxfRoot[0]/O14[0]/O4{A1=<seitenA1>}/<pathFromPage>/O17{A1=<geräteA1>}/...

### Wichtige Attribut-Codes auf O17
- A1 = Referenz-ID des Geräts (z.B. "17/7")

### Geräteeigenschaften in P11 (Kindknoten von O17)
- P20011 = Funktionstext (Format: \`??_??@TEXT;\`)
  - Nur den Text nach @ und vor ; ändern, z.B. "??_??@NEUER TEXT;"
- P20100_1 = Artikelnummer (z.B. "PXC.2901362")
- P20101_1 = Artikelnummer Typ (meistens "1")
- P20102_1 = Artikelnummer Zähler (meistens "1")

### Positionsattribute in S40x1201 (Kindknoten von O17)
- A762 = X/Y-Position im Format \`"X/Y"\`, z.B. \`"44/232"\` bedeutet X=44, Y=232
  - NIEMALS das \`??_??@...;\`-Format verwenden — das gilt NUR für P20011!
  - Um Y zu ändern: alten Wert aus childNodes lesen, Y-Teil berechnen, neuen String setzen
  - Beispiel: aktuell "44/232", 5 Einheiten nach oben → neuer Y-Wert = 232 - 5 = 227 → "44/227"
  - In EPLAN: Y wächst nach unten. "Nach oben" = Y verkleinern, "nach unten" = Y vergrößern.
- A763 = Breite, A764 = Höhe (einzelner Zahlenwert als String, z.B. "50")

### BMK (Betriebsmittelkennzeichen) in P144 und P150
- P20013 = BMK-Prefix (ein oder mehrere Buchstaben, z.B. "K", "QA", "PF")
- P20014 = BMK-Nummer (z.B. "1", "10", "100")
- Vollständiger BMK = P20013 + P20014, z.B. "QA" + "10" = "QA10"
- P144 = BMK Hauptfunktion
- P150 = BMK Einbauort (oft gleiche Werte wie P144)
- WICHTIG: Beim BMK-Ändern immer BEIDE P144 und P150 aktualisieren!
- KRITISCH: Lies den exakten Prefix (bmkPrefix) und die Nummer (bmkNumber) aus dem "devices"-Array des Geräts in der Dateistruktur — verwende NIEMALS den Prefix aus der BMK-Tabelle unten!
  - Beispiel: Gerät hat "bmkPrefix": "QA", "bmkNumber": "10" → Änderung auf QA100 = P20013="QA", P20014="100"
  - Beispiel: Gerät hat "bmkPrefix": "PF", "bmkNumber": "1" → Änderung auf K5 = P20013="K", P20014="5"

### BMK-Buchstabencode (DIN EN 81346) — NUR zur Referenz, NICHT für Pfadgenerierung
- K = Schütz/Relais, Q/QA/QB = Leistungsschalter/Motorschutz, F = Sicherung, S = Schalter/Taster
- X/XD = Klemme, M = Motor, PF = Schutzgerät, A = Baugruppe

## Beispiel: Artikelnummer eines Geräts ändern
\`\`\`json
{
  "op": "set-attribute",
  "path": "${exampleDevicePath}/P11[0]",
  "attribute": "P20100_1",
  "value": "6ES7 511-1AK02-0AB0"
}
\`\`\`

## Beispiel: BMK eines Geräts von PF1 auf K5 ändern
\`\`\`json
[
  { "op": "set-attribute", "path": "${exampleDevicePath}/P144[0]", "attribute": "P20013", "value": "K" },
  { "op": "set-attribute", "path": "${exampleDevicePath}/P144[0]", "attribute": "P20014", "value": "5" },
  { "op": "set-attribute", "path": "${exampleDevicePath}/P150[0]", "attribute": "P20013", "value": "K" },
  { "op": "set-attribute", "path": "${exampleDevicePath}/P150[0]", "attribute": "P20014", "value": "5" }
]
\`\`\`

## Beispiel: Funktionstext ändern
\`\`\`json
{
  "op": "set-attribute",
  "path": "${exampleDevicePath}/P11[0]",
  "attribute": "P20011",
  "value": "??_??@Neuer Funktionstext;"
}
\`\`\`

## Attribut-Referenz (Hinweis: Zuordnungen sind näherungsweise, nicht garantiert korrekt)
${Object.entries(EPLAN_ATTRIBUTE_LABELS).map(([k, v]) => `${k}=${v}`).join('  ')}

## Dateistruktur
${JSON.stringify(fileOverview?.overview || [], null, 2).substring(0, 20000)}
`;

    if (ragContext) {
        prompt += `\n## Zusätzlicher Kontext aus Dokumenten\n${ragContext}\n`;
    }

    prompt += `\nVERWENDE die Dateistruktur oben, um korrekte Pfade zu erstellen.
Jede O4-Seite hat ein "devices"-Array mit allen O17-Geräten. Jedes Gerät hat:
- "attributes.A1": Die Referenz-ID für den Pfad
- "bmk": Der vollständige BMK (z.B. "QA10")
- "bmkPrefix": Der exakte Prefix-Wert für P20013 (z.B. "QA") — IMMER direkt verwenden!
- "bmkNumber": Der exakte Nummern-Wert für P20014 (z.B. "10") — IMMER direkt verwenden!
- "article": Die Artikelnummer
- "pathFromPage": (optional) Der Zwischenpfad von O4 zum Gerät durch O26-Container
- "childNodes": Array aller direkten Kindknoten von O17, jeweils mit { tag, attrs }.
  Beispiel: { tag: "S40x1201", attrs: { A762: "100;200", A763: "50" } }
  → Pfad zum Attribut: <gerätepfad>/S40x1201[0], Attribut: "A762"
  → Verwende immer den Pfad aus childNodes, NICHT geraten!

PFAD-AUFBAU für ein Gerät:
1. Durchsuche ALLE "devices"-Arrays ALLER O4-Seiten nach dem gesuchten BMK
2. Nimm den A1-Wert der gefundenen Seite (O4) und des Geräts (O17)
3. Wenn das Gerät ein "pathFromPage" hat, MUSS es im Pfad zwischen O4 und O17 stehen:
   /EplanPxfRoot[0]/O14[0]/O4{A1=<seitenA1>}/<pathFromPage>/O17{A1=<geräteA1>}/...
4. Ohne "pathFromPage": /EplanPxfRoot[0]/O14[0]/O4{A1=<seitenA1>}/O17{A1=<geräteA1>}/...

BMK-ÄNDERUNG — PFLICHTREGELN:
- Lies "bmkPrefix" und "bmkNumber" aus dem Geräteeintrag in der Dateistruktur
- Ändere NUR was der Nutzer verlangt: ändert sich nur die Nummer, bleibt bmkPrefix GLEICH
- Beispiel: "QA10" → "QA100": P20013 bleibt "QA", P20014 wird "100" (NICHT P20013="Q"!)
- Setze IMMER beide P144 und P150 auf die neuen Werte

Antworte IMMER im JSON-Format mit "text" und "operations". Verwende NIEMALS abgekürzte Pfade mit "..." — gib immer den vollständigen Pfad ab /EplanPxfRoot an.`;

    return prompt;
}

// ─── Tool-calling agent ───────────────────────────────────────────────────────

/**
 * OpenAI tool definitions for EPJ editing.
 * The agent calls these to look up devices, then calls submit_operations when done.
 */
export const EPJ_TOOLS = [
    {
        type: 'function',
        function: {
            name: 'find_device',
            description: 'Suche ein Gerät anhand des BMK. Gibt Pfad, bmkPrefix, bmkNumber und childNodes zurück. Wenn das BMK auf mehreren Seiten vorkommt, wird ein "matches"-Array zurückgegeben — dann die passende Seite anhand pageDescription oder pageA1 auswählen.',
            parameters: {
                type: 'object',
                properties: {
                    bmk: { type: 'string', description: 'BMK des gesuchten Geräts, z.B. "QA10", "K5", "XD1"' }
                },
                required: ['bmk']
            }
        }
    },
    {
        type: 'function',
        function: {
            name: 'list_devices',
            description: 'Liste alle Geräte in der EPJ-Datei mit BMK, Pfad und Artikelnummer auf.',
            parameters: { type: 'object', properties: {}, required: [] }
        }
    },
    {
        type: 'function',
        function: {
            name: 'get_file_info',
            description: 'Gibt eine Übersicht der EPJ-Datei zurück: Seitenanzahl, Seitenliste (A1, Beschreibung), Geräteanzahl. Nützlich für Fragen wie "Wie viele Seiten?" oder "Welche Seiten gibt es?".',
            parameters: { type: 'object', properties: {}, required: [] }
        }
    },
    {
        type: 'function',
        function: {
            name: 'submit_operations',
            description: 'Reiche die fertigen EPLAN-Bearbeitungsoperationen ein. PFLICHT: "operations" muss ein nicht-leeres Array mit den konkreten Bearbeitungsbefehlen enthalten (set-attribute, copy-node, remove-node). Nur aufrufen wenn alle Gerätedaten per find_device ermittelt wurden.',
            parameters: {
                type: 'object',
                properties: {
                    text: { type: 'string', description: 'Kurze Beschreibung der Änderungen auf Deutsch' },
                    operations: {
                        type: 'array',
                        description: 'PFLICHTFELD: Array der konkreten Bearbeitungsbefehle. Jeder Eintrag ist ein Objekt mit "op", "path" und weiteren Feldern je nach Operationstyp. Darf NICHT leer sein.',
                        items: {
                            type: 'object',
                            properties: {
                                op: { type: 'string', enum: ['set-attribute', 'copy-node', 'remove-node'] },
                                path: { type: 'string' }
                            },
                            required: ['op']
                        },
                        minItems: 1
                    }
                },
                required: ['text', 'operations']
            }
        }
    }
];

/**
 * Flatten all devices from the overview into a lookup map keyed by BMK.
 * A BMK may appear on multiple pages (e.g. circuit + terminal diagrams),
 * so each key maps to an array of matches.
 * @param {Object} fileOverview
 * @returns {Map<string, Array>} bmk → array of device objects with path + pageA1
 */
function buildDeviceMap(fileOverview) {
    const map = new Map();
    try {
        for (const item of fileOverview?.overview || []) {
            if (item.tag !== 'EplanPxfRoot' || !item.children) continue;
            for (const child of item.children) {
                if (child.tag !== 'O14' || !child.children) continue;
                for (const page of child.children) {
                    if (page.tag !== 'O4' || !page.attributes?.A1 || !page.devices) continue;
                    const pageA1 = page.attributes.A1;
                    const pageDescription = page.pageName || page.attributes?.A4 || page.attributes?.P20027 || '';
                    for (const dev of page.devices) {
                        if (!dev.bmk) continue;
                        const deviceA1 = dev.attributes?.A1 || '';
                        const path = dev.pathFromPage
                            ? `/EplanPxfRoot[0]/O14[0]/O4{A1=${pageA1}}/${dev.pathFromPage}/O17{A1=${deviceA1}}`
                            : `/EplanPxfRoot[0]/O14[0]/O4{A1=${pageA1}}/O17{A1=${deviceA1}}`;
                        const pagePath = `/EplanPxfRoot[0]/O14[0]/O4{A1=${pageA1}}`;
                        const entry = { ...dev, path, pagePath, pageA1, pageDescription };
                        if (!map.has(dev.bmk)) map.set(dev.bmk, []);
                        map.get(dev.bmk).push(entry);
                    }
                }
                break;
            }
            break;
        }
    } catch { /* ignore */ }
    return map;
}

/**
 * Build a lean system prompt for the tool-calling agent.
 * No device index or file structure dump — the agent uses tools for lookup.
 */
export function buildEpjAgentPrompt(fileName, ragContext = null) {
    let prompt = `Du bist ein EPLAN P8 Experte. Du arbeitest mit der EPJ-Datei "${fileName}".

Du kannst zwei Dinge tun:
1. Fragen zur Datei beantworten — nutze get_file_info, list_devices oder find_device und antworte dann direkt (KEIN submit_operations).
2. Änderungen vornehmen — ermittle zuerst exakte Pfade per find_device/list_devices, dann submit_operations.

WICHTIG: Du darfst KEIN rohes XML erzeugen und keine Pfade erfinden.

## Erlaubte Operationen

### set-attribute — Attributwert setzen
{ "op": "set-attribute", "path": "<pfad zum Kindknoten>", "attribute": "<code>", "value": "<wert>" }

### copy-node — Gerät kopieren (PFLICHT für neue Geräte, NIEMALS add-child)
{
  "op": "copy-node",
  "sourcePath": "<pfad zum Quellgerät>",
  "targetPath": "<pfad zur Zielseite>",
  "attributeOverrides": { "A1": "<neue ID, z.B. Quell-A1 + 1>" },
  "childAttributeOverrides": [
    { "childTag": "P144", "attributes": { "P20013": "<prefix>", "P20014": "<nummer>" } },
    { "childTag": "P150", "attributes": { "P20013": "<prefix>", "P20014": "<nummer>" } },
    { "childTag": "S40x1201", "attributes": { "A762": "<neue Position X/Y>" } }
  ]
}
WICHTIG: Alle Änderungen am neuen Gerät (BMK, Position) MÜSSEN in childAttributeOverrides stehen.
NIEMALS nach copy-node ein separates set-attribute auf das neue Gerät anwenden — die A1 des neuen Knotens ist zur Planungszeit nicht bekannt.

### remove-node — Knoten entfernen
{ "op": "remove-node", "path": "<pfad>" }

## Kindknoten von O17 (aus find_device → childNodes)
Jedes Gerät hat Kindknoten wie P11, P144, P150, S40x1201 usw.
Pfad zu einem Kindknoten: <gerätepfad>/<tag>[0]
Beispiel: .../O17{A1=17/4524}/S40x1201[0]

## Wichtige Attribute
- P11: P20011=Funktionstext (Format "??_??@TEXT;"), P20100_1=Artikelnummer
- P144/P150: P20013=BMK-Prefix, P20014=BMK-Nummer
- S40x1201: A762=Position "X/Y" (z.B. "44/232"), Y wächst nach OBEN (größerer Y = weiter oben). Nach unten verschieben = Y verkleinern. Nach oben verschieben = Y erhöhen.
- A1: Referenz-ID eines Knotens

## BMK-Regeln
- Prefix (P20013) und Nummer (P20014) IMMER aus find_device-Ergebnis lesen
- Beispiel QA10→QA100: P20013="QA" bleibt gleich, P20014="100"
- IMMER beide P144 und P150 aktualisieren`;

    if (ragContext) {
        prompt += `\n\n## Kontext aus Dokumenten\n${ragContext}`;
    }

    return prompt;
}

/**
 * Create a tool executor function for the EPJ agent.
 * Executes find_device and list_devices locally using the already-loaded overview.
 * submit_operations is handled by chatWithTools as the finish signal.
 *
 * @param {Object} fileOverview - from getEpjOverview()
 * @returns {Function} async (name, args) => result
 */
export function createEpjToolExecutor(fileOverview) {
    const deviceMap = buildDeviceMap(fileOverview);

    return async function executeEpjTool(name, args) {
        if (name === 'find_device') {
            const matches = deviceMap.get(args.bmk);
            if (!matches) {
                const available = [...deviceMap.keys()].slice(0, 20).join(', ');
                return { error: `Gerät "${args.bmk}" nicht gefunden. Verfügbare BMKs: ${available}` };
            }
            // Single match — return directly (most common case)
            if (matches.length === 1) return matches[0];
            // Multiple matches — return all with page context
            // Include guidance so the AI knows when to apply to all vs. ask
            return {
                multipleMatches: true,
                count: matches.length,
                guidance: 'Dieses Gerät existiert auf mehreren Seiten (typisch in EPLAN: Stromlaufplan + Klemmenpläne). Regel: (1) BMK-Änderungen und Artikelnummern → auf ALLEN Seiten ändern. (2) Positions- oder seitenspezifische Änderungen → nur die passende Seite (in der Regel die erste / niedrigste pageA1). (3) Löschen → NUR wenn explizit eine Seite genannt wurde, sonst alle löschen.',
                matches: matches.map(d => ({
                    path: d.path,
                    pagePath: d.pagePath,
                    pageA1: d.pageA1,
                    pageDescription: d.pageDescription,
                    childNodes: d.childNodes
                }))
            };
        }

        if (name === 'get_file_info') {
            const pages = [];
            try {
                for (const item of fileOverview?.overview || []) {
                    if (item.tag !== 'EplanPxfRoot' || !item.children) continue;
                    for (const child of item.children) {
                        if (child.tag !== 'O14' || !child.children) continue;
                        for (const page of child.children) {
                            if (page.tag !== 'O4') continue;
                            pages.push({
                                pageA1: page.attributes?.A1 || '',
                                description: page.pageName || page.attributes?.A4 || page.attributes?.P20027 || '',
                                deviceCount: page.devices?.length || 0
                            });
                        }
                        break;
                    }
                    break;
                }
            } catch { /* ignore */ }
            return {
                fileName: fileOverview?.fileName,
                pageCount: pages.length,
                totalDevices: [...deviceMap.values()].reduce((s, arr) => s + arr.length, 0),
                pages
            };
        }

        if (name === 'list_devices') {
            const devices = [];
            for (const entries of deviceMap.values()) {
                for (const d of entries) {
                    devices.push({
                        bmk: d.bmk,
                        path: d.path,
                        pagePath: d.pagePath,
                        pageA1: d.pageA1,
                        bmkPrefix: d.bmkPrefix,
                        bmkNumber: d.bmkNumber,
                        article: d.article || null,
                        functionText: d.functionText || null
                    });
                }
            }
            return { devices };
        }

        return { error: `Unbekanntes Tool: ${name}` };
    };
}
